import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/BusinessProfileContext";
import PageHeader from "@/components/PageHeader";
import { eur } from "@/components/LineItemsEditor";
import { Sun, Loader2, Calendar, CheckSquare, Receipt, FileText, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function Today() {
  const { vocabulary } = useProfile();
  const apptLabel = vocabulary.appointment || "Rendez-vous";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setData((await api.get("/dashboard/today")).data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  const c = data?.counts || {};

  const Card = ({ icon: Icon, title, count, color, children, testid }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></div>
        <h3 className="font-heading font-semibold text-slate-900 text-sm">{title}</h3>
        <span className="ml-auto text-xs font-bold text-slate-400">{count}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div>
      <PageHeader title="Aujourd'hui" subtitle={new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} icon={Sun} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card icon={Calendar} title={`${apptLabel}s du jour`} count={c.appointments || 0} color="bg-blue-500/10 text-blue-600" testid="today-appointments">
          {(data.appointments || []).length === 0 ? <p className="text-sm text-slate-400">Aucun pour aujourd'hui.</p> :
            <ul className="space-y-1.5">{data.appointments.map((a) => (
              <li key={a.id} className="text-sm flex items-center gap-2"><span className="font-mono text-slate-500">{(a.start || "").slice(11, 16)}</span><Link to="/calendar" className="text-slate-700 hover:text-emerald-600">{a.client_name || a.title || "—"}</Link></li>
            ))}</ul>}
        </Card>
        <Card icon={CheckSquare} title="Tâches à faire" count={c.tasks || 0} color="bg-amber-500/10 text-amber-600" testid="today-tasks">
          {(data.tasks || []).length === 0 ? <p className="text-sm text-slate-400">Aucune tâche en attente.</p> :
            <ul className="space-y-1.5">{data.tasks.map((t) => (
              <li key={t.id} className="text-sm flex items-center gap-2"><Link to="/taches" className="text-slate-700 hover:text-emerald-600">{t.title}</Link>{t.due_date && <span className="text-xs text-slate-400">· {t.due_date}</span>}</li>
            ))}</ul>}
        </Card>
        <Card icon={Receipt} title="Factures impayées" count={c.unpaid || 0} color="bg-red-500/10 text-red-600" testid="today-unpaid">
          {(data.unpaid_invoices || []).length === 0 ? <p className="text-sm text-slate-400">Rien à relancer.</p> :
            <ul className="space-y-1.5">{data.unpaid_invoices.slice(0, 8).map((i) => (
              <li key={i.id} className="text-sm flex items-center gap-2"><Link to="/impayes" className="text-slate-700 hover:text-emerald-600">{i.number} · {i.client_name || "—"}</Link><span className="ml-auto font-mono text-red-600">{eur(i.total || 0)}</span></li>
            ))}</ul>}
        </Card>
        <Card icon={FileText} title="Devis à relancer" count={c.quotes || 0} color="bg-violet-500/10 text-violet-600" testid="today-quotes">
          {(data.quotes_to_follow || []).length === 0 ? <p className="text-sm text-slate-400">Aucun devis en attente.</p> :
            <ul className="space-y-1.5">{data.quotes_to_follow.slice(0, 8).map((q) => (
              <li key={q.id} className="text-sm flex items-center gap-2"><Link to="/quotes" className="text-slate-700 hover:text-emerald-600">{q.number} · {q.client_name || "—"}</Link><span className="ml-auto font-mono text-slate-500">{eur(q.total || 0)}</span></li>
            ))}</ul>}
        </Card>
        {(data.contracts_expiring || []).length > 0 && (
          <Card icon={Repeat} title="Contrats à surveiller" count={c.contracts_expiring || 0} color="bg-emerald-500/10 text-emerald-600" testid="today-contracts">
            <ul className="space-y-1.5">{data.contracts_expiring.map((ct) => (
              <li key={ct.id} className="text-sm flex items-center gap-2"><Link to="/contrats" className="text-slate-700 hover:text-emerald-600">{ct.title} · {ct.client_name || "—"}</Link><span className="ml-auto text-xs text-amber-600">échéance {ct.end_date}</span></li>
            ))}</ul>
          </Card>
        )}
      </div>
    </div>
  );
}
